import React from 'react';
import { useGetAllVendorSettingsQuery, useDeleteVendorSettingsMutation } from '../../store/Api';
import {
    useUpdateOrganizationFeatureSettingsMutation,
    useGetOrganizationFeatureSettingsQuery
} from '../../store/endpoints/OrganizationsEndpoints';
import { IVendorSettings } from '../../models/Settings';
import {
    CommandBar,
    DefaultButton,
    Dropdown,
    IColumn,
    ICommandBarItemProps,
    Icon,
    SelectionMode,
    ShimmeredDetailsList
} from '@fluentui/react';
import { ButtonColumnGenerator, ColumnGenerator } from '../../logic/Columns/ColumnGenerator';
import VendorManagementEditPanel from '../../components/Vendors/VendorManagementEditPanel';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useUrlPanelState } from '../../hooks/useUrlState';

const VendorManagementPage: React.FC = () => {
    const vendors = useGetAllVendorSettingsQuery();
    /*
     * Editing an existing vendor goes through the URL (?panel=vendor&instance=<sourceVendor>) so a
     * link can open it; "Add" stays local, having no vendor to name yet.
     */
    const vendorPanel = useUrlPanelState('vendor');
    const [creating, setCreating] = React.useState(false);
    const selectedVendor = creating
        ? undefined
        : vendors.data?.items?.find((v: IVendorSettings) => v.sourceVendor === vendorPanel.instanceId);
    const modalOpen = vendorPanel.isOpen && (creating || !!selectedVendor);
    const selectedOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const [excludeFromBestPrice, setExcludeFromBestPrice] = React.useState<string[]>([]);
    const [savingOrg, setSavingOrg] = React.useState(false);
    const [updateOrgFeatureSettings] = useUpdateOrganizationFeatureSettingsMutation();
    const [deleteVendor] = useDeleteVendorSettingsMutation();
    const getOrganizationFeatureSettings = useGetOrganizationFeatureSettingsQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''});
    
    React.useEffect(() => {
        if (getOrganizationFeatureSettings.data) {
            setExcludeFromBestPrice(getOrganizationFeatureSettings.data.excludeFromBestPrice ?? []);
        }
    }, [getOrganizationFeatureSettings.data]);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'Add',
            text: 'Add',
            iconProps: { iconName: 'Add' },
            onClick: () => {
                setCreating(true);
                vendorPanel.open();
            }
        }
    ]

    const farComands: ICommandBarItemProps[] = [
        {
            key: 'Refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            iconOnly: true,
            onClick: () => {vendors.refetch();}
        }
    ]

    const selectVendor = (v: IVendorSettings) => {
        setCreating(false);
        vendorPanel.open(v.sourceVendor);
    }

    const columns: IColumn[] = [
        ColumnGenerator('Vendor Name', 'sourceVendor', 100),
        ColumnGenerator('Display Name', 'vendorDisplayName', 200, 200),
        ColumnGenerator('Punchout Name', 'punchoutVendorName', 200, 200),
        ColumnGenerator('Enabled', 'enabled', 100, 100, (v: IVendorSettings) => v.enabled ? <Icon iconName='CheckMark' /> : <></>),
        ButtonColumnGenerator(selectVendor, 'Edit', 'Edit'),
        ColumnGenerator('', 'delete', 100, 100, (vendor: IVendorSettings) => {
            return <DefaultButton
                text='Delete'
                className="btn-warning"
                onClick={() => deleteVendor(vendor.sourceVendor)}
            />;
        })
    ]

    const saveOrg = async () => {
        setSavingOrg(true);
        if (selectedOrg && getOrganizationFeatureSettings.data && (
            getOrganizationFeatureSettings.data.excludeFromBestPrice !== excludeFromBestPrice
        )) {
            await updateOrgFeatureSettings({
                    request: {
                        id: selectedOrg,
                        excludeFromBestPrice
                    }, 
                id: selectedOrg
            })
        }
        setSavingOrg(false);
    }

    return (
        <div className='main-content'>
            <h1>Vendor Management</h1>
            <CommandBar items={commands} farItems={farComands} />
            <ShimmeredDetailsList items={vendors.data?.items ?? []} enableShimmer={vendors.isFetching} columns={columns} selectionMode={SelectionMode.none} />
            <VendorManagementEditPanel vendor={selectedVendor} isOpen={modalOpen} onClose={() => { setCreating(false); vendorPanel.close(); }} />
            <h3>Best Price Column</h3>
            <div data-setting-id='search.settings.excludeFromBestPrice'>
                <Dropdown
                    label='Exclude from Best Price'
                    multiSelect
                    selectedKeys={excludeFromBestPrice}
                    options={vendors.data?.items?.map(v => ({key: v.sourceVendor, text: v.vendorDisplayName ?? v.sourceVendor})) ?? []}
                    disabled={savingOrg || vendors.isLoading}
                    onChange={(e,item) => {
                        if (!item) return;
                        setExcludeFromBestPrice(
                            item.selected ? [...excludeFromBestPrice, item.key as string] : excludeFromBestPrice.filter(key => key !== item.key),
                        );
                    }}
                    onBlur={saveOrg}
                    />
            </div>
        </div>
    );
}

export default VendorManagementPage;